import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FormGroup, FormText, Input, Label } from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value. Radios are commonly used for yes/no and numeric
	 * choices, so booleans and numbers are valid alongside strings (options
	 * are matched with strict equality).
	 */
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

	/**
	 * The input options (if any).
	 */
	options: PropTypes.arrayOf(PropTypes.shape({

		id: PropTypes.string,

		label: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.element,
			PropTypes.array,
			PropTypes.shape({}),
		]),

		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

		help: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

		weight: PropTypes.number,

		disabled: PropTypes.bool,

	})),

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const RadioInput = (props) => {

	const {
		name,
		value,
		options,
		isEditing,
		isInvalid,
		disabled,
		onChange,
	} = props;

	if (!isEditing) {

		const matchingOption =
			options?.find((option) => option.value === value);

		return (
			<div className="tc-material-input-display tc-material-input-display-radio">
				{value &&
					<FormGroup check>
						<Input
							name={`option-display-${name}`}
							type="radio"
							value={value}
							disabled
							checked />
						{' '}
						<Label
							for={`option-display-${name}`}
							check>
							{matchingOption?.label || value}
						</Label>
					</FormGroup>
				}
				{!value &&
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
			</div>
		);

	}

	// Sort the options!
	const sortedOptions = options?.sort((option1, option2) => {

		// Support for options that haven't been migrated to use weights yet!
		if (option1.weight === undefined ||
			option1.weight === null ||
			option2.weight === undefined ||
			option2.weight === null) {
			return 0;
		}

		return option1.weight - option2.weight;

	});

	return (
		<div
			role="radiogroup"
			className={`tc-material-input-radio ${isInvalid ? 'tc-material-input-invalid' : ''}`}>
			{sortedOptions?.map((option) => (
				<FormGroup
					key={option.id}
					className={`
						tc-material-input-radio-option-${option.id}
						${option.value === value ? 'tc-material-input-radio-option-is-selected' : ''}
					`}
					check>
					<Input
						id={option.id}
						name={name}
						type="radio"
						role="radio"
						value={option.value}
						checked={option.value === value}
						invalid={isInvalid}
						disabled={disabled || option.disabled}
						onChange={(event) => onChange(event.target.value, option.id)} />
					{' '}
					<Label
						for={option.id}
						check>
						<div className="d-flex flex-column">
							<span>{option.label || option.value}</span>
							{option.help &&
								<FormText className="mt-0">
									{option.help}
								</FormText>
							}
						</div>
					</Label>
				</FormGroup>
			))}
		</div>
	);
};

RadioInput.propTypes = propTypes;

export default RadioInput;
